/**
 * Early Access form -> STOQ (Restock Rocket) back-in-stock subscription.
 *
 * Primary path (PATH A — Create Intent HTTP API):
 *   STOQ documents a public, key-less endpoint built for front-end forms:
 *     POST https://app.stoqapp.com/api/v1/intents.json
 *     headers: Content-Type: application/json, X-Shopify-Shop-Domain: <shop>.myshopify.com
 *     body: { intent: { shopify_variant_id, shopify_product_id, channel:"email",
 *                       quantity:1, source:"api" }, customer: { email, name } }
 *   This creates a REAL subscription for the target variant and lets us keep the
 *   customer on this page with our own success message — no modal, no redirect.
 *
 * Fallback path (PATH B — JS API modal handoff):
 *   If the POST is blocked (e.g. CORS) we hand off to STOQ's own modal via
 *   window._RestockRocket.openModal(productData, variantId, customerData), passing
 *   a productData OBJECT carrying a variants array (a bare id crashes openModal's
 *   internal .variants.filter()). The 3rd arg pre-fills the captured name/email.
 *
 * No STOQ code is touched. The STOQ->Discord auto-redirect is suppressed for our
 * flow via window.__eaStoqBridgeActive (read by stoq-discord-redirect.js).
 * Variant / product ids + shop domain come from data-ea-* on .ea-form-area.
 */
(function () {
  'use strict';

  var CFG = {
    formSelector: '.ea-form',
    emailSelector: 'input[type="email"]',
    nameSelector: 'input[name="contact[first_name]"]',
    submitSelector: '.ea-submit',
    consentSelector: '.ea-consent__check',
    areaSelector: '.ea-form-area',
    sectionSelector: '.ea-section',
    variantAttr: 'data-ea-notify-variant',
    productAttr: 'data-ea-notify-product',
    shopAttr: 'data-ea-shop',
    intentUrl: 'https://app.stoqapp.com/api/v1/intents.json',
    successText: "You're on the list! We'll email you the moment it's available.",
    duplicateText: "You're already on the list — we'll email you the moment it's available."
  };

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var bound = false;

  function getStoq() { return window._RestockRocket || window._Stoq || null; }

  function setBtn(btn, html, disabled) {
    if (!btn) return;
    btn.disabled = !!disabled;
    if (html != null) btn.innerHTML = html;
  }

  function showMessage(form, success, text) {
    var area = form.closest(CFG.areaSelector) || form.parentNode;
    if (!area) return;
    if (success) {
      area.innerHTML =
        '<div class="ea-form-success" role="status" style="display:flex;align-items:center;gap:10px;font-weight:600;">' +
        '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>' +
        '<span>' + text + '</span></div>';
    } else {
      var err = area.querySelector('.ea-form-error');
      if (!err) {
        err = document.createElement('p');
        err.className = 'ea-form-error';
        err.setAttribute('role', 'alert');
        err.style.cssText = 'margin-top:10px;color:#d62a20;font-size:.875rem;font-weight:600;';
        area.appendChild(err);
      }
      err.textContent = text;
    }
  }

  /* PATH A — POST to STOQ's Create Intent endpoint. Returns a Promise that
     resolves true on success/duplicate, or rejects so the caller can fall back. */
  function createIntent(shop, variantId, productId, email, name) {
    var intent = {
      shopify_variant_id: Number(variantId),
      channel: 'email',
      quantity: 1,
      source: 'api'
    };
    if (productId) intent.shopify_product_id = Number(productId);

    var customer = { email: email };
    if (name) customer.name = name;

    return fetch(CFG.intentUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Shop-Domain': shop
      },
      body: JSON.stringify({ intent: intent, customer: customer })
    }).then(function (res) {
      // 2xx => created. 409/422 typically => already subscribed (still a "win").
      if (res.ok) return { ok: true, duplicate: false };
      if (res.status === 409 || res.status === 422) return { ok: true, duplicate: true };
      throw new Error('intent http ' + res.status);
    });
  }

  /* PATH B — hand off to STOQ's modal with a proper productData object. STOQ's
     openModal(productData, variantId, customerData) needs productData.variants to
     be an array; a bare id throws. We supply the minimum documented shape. */
  function openModalFallback(variantId, productId, email, name) {
    var stoq = getStoq();
    if (!stoq || typeof stoq.openModal !== 'function') return false;
    var vid = String(variantId);
    var productData = {
      id: productId ? Number(productId) : undefined,
      variants: [{ id: Number(variantId), available: false }]
    };
    var customerData = { email: email };
    if (name) customerData.name = name;
    try {
      // Let STOQ's modal drive the redirect/normal flow for this fallback.
      window.__eaStoqBridgeActive = false;
      stoq.openModal(productData, vid, customerData);
      return true;
    } catch (e) {
      return false;
    }
  }

  function onSubmit(e) {
    var form = e.target && e.target.closest ? e.target.closest(CFG.formSelector) : null;
    if (!form) return;
    e.preventDefault();

    var emailInput = form.querySelector(CFG.emailSelector);
    var email = emailInput ? String(emailInput.value).trim() : '';
    if (!EMAIL_RE.test(email)) {
      showMessage(form, false, 'Please enter a valid email address.');
      if (emailInput) emailInput.focus();
      return;
    }

    var nameInput = form.querySelector(CFG.nameSelector);
    var name = nameInput ? String(nameInput.value).trim() : '';

    var section = form.closest(CFG.sectionSelector) || document;
    var consent = section.querySelector(CFG.consentSelector);
    if (consent && !consent.checked) {
      showMessage(form, false, 'Please agree to receive emails to continue.');
      return;
    }

    var area = form.closest(CFG.areaSelector);
    var variantId = area && area.getAttribute(CFG.variantAttr);
    var productId = area && area.getAttribute(CFG.productAttr);
    var shop = (area && area.getAttribute(CFG.shopAttr)) ||
      (window.Shopify && window.Shopify.shop) || '';

    if (!variantId) {
      showMessage(form, false, 'This form isn\'t linked to a product yet. Set the "Notify-me product" in the theme editor.');
      return;
    }
    if (!shop) {
      showMessage(form, false, 'Notifications aren\'t configured yet — please try again later.');
      return;
    }

    var btn = form.querySelector(CFG.submitSelector);
    var origHtml = btn ? btn.innerHTML : '';
    setBtn(btn, 'Submitting…', true);

    // Suppress the Discord auto-redirect if PATH B ends up firing the success event.
    window.__eaStoqBridgeActive = true;

    createIntent(shop, variantId, productId, email, name)
      .then(function (r) {
        window.__eaStoqBridgeActive = false;
        showMessage(form, true, r.duplicate ? CFG.duplicateText : CFG.successText);
      })
      .catch(function () {
        // POST blocked (likely CORS) — fall back to STOQ's modal, prefilled.
        var opened = openModalFallback(variantId, productId, email, name);
        if (opened) {
          // STOQ's modal now owns the flow; restore the button in case the user closes it.
          setBtn(btn, origHtml, false);
        } else {
          window.__eaStoqBridgeActive = false;
          setBtn(btn, origHtml, false);
          showMessage(form, false, 'We couldn\'t reach the notify service — please try again in a moment.');
        }
      });
  }

  function init() {
    if (bound) return;
    bound = true;
    document.addEventListener('submit', onSubmit, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
